import React from 'react';
import { AutoCompleteSelectUI, DatePickerUI, DebouncedInputUI, FormLabelUI, SwitchUI } from '../Interface';
import { sxConfig } from './kanban.style';
import { getKanbanFieldsByControl } from '../../utils/helper';
import dayjs from 'dayjs';
import { TaskResponsibleTypeEnum } from '../../config/taskList';
import useParam from '../../context/ParamContext';

const KanbanFilters = ({
  type,
  filterFormState,
  autoCompleteOptions,
  responsibleType,
  roles,
  handleSimpleFilterChange,
  handleAutocompleteChange,
  handleSwitchChange,
}) => {
  const { params } = useParam();

  return (
    <>
      <div className="w-full md:w-[110px]">
        {getKanbanFieldsByControl(type, 'textLike').map(([fieldName, cfg]) => (
          <DebouncedInputUI
            key={fieldName}
            label={<FormLabelUI text={cfg.label} textColor="xl:text-white" />}
            debounceMs={800}
            fullWidth
            fieldName={fieldName}
            setQuickFilterSearchValue={handleSimpleFilterChange}
            quickFilterSearchValue={filterFormState[fieldName]}
            sx={{
              '@media (min-width:1140px)': {
                ...sxConfig.fieldColor,
              },
            }}
          />
        ))}
      </div>
      {getKanbanFieldsByControl(type, 'autocomplete').map(([fieldName, cfg]) => {
        const disabled = cfg.getDisabled(roles);

        if (cfg.shouldShow && !cfg.shouldShow(params, type)) return null;

        const fieldByType = {
          [TaskResponsibleTypeEnum.USER]: 'responsiblePersonId',
          [TaskResponsibleTypeEnum.USERGROUP]: 'responsibleUserGroupId',
          [TaskResponsibleTypeEnum.BUSINESSPARTNER]: 'responsibleCompanyId',
          [TaskResponsibleTypeEnum.WORKER]: 'responsibleWorkerId',
        };

        const usedFieldName = fieldName === 'responsible' ? fieldByType[responsibleType] : fieldName;

        return (
          <div key={fieldName} className={cfg.widthClass}>
            <AutoCompleteSelectUI
              variant="standard"
              onChange={(_e, newVal, reason) => handleAutocompleteChange(fieldName, newVal, reason, cfg.control)}
              selectedValue={filterFormState[usedFieldName]}
              selectedLabelValue={
                fieldName === 'responsiblePersonId'
                  ? autoCompleteOptions[cfg.optionsKey]?.find(
                      (o) => o.value === filterFormState[usedFieldName] && o.type === responsibleType
                    )?.label
                  : autoCompleteOptions[cfg.optionsKey]?.find((o) => o.value === filterFormState[usedFieldName])?.label
              }
              label={<FormLabelUI text={cfg.label} textColor="xl:text-white" />}
              dataset={autoCompleteOptions[cfg.optionsKey]}
              disabled={disabled}
              sx={{
                '@media (min-width:1140px)': {
                  ...sxConfig.fieldColor,
                },
              }}
            />
          </div>
        );
      })}
      {getKanbanFieldsByControl(type, 'multiAutocomplete').map(([fieldName, cfg]) => {
        const disabled = cfg.getDisabled(roles);

        const selectedValues = (filterFormState[fieldName] ?? []).map((id) =>
          autoCompleteOptions[cfg.optionsKey]?.find((o) => o.value === id)
        );

        if (cfg.shouldShow && !cfg.shouldShow(params, type)) return null;

        return (
          <div key={fieldName} className={cfg.widthClass}>
            <AutoCompleteSelectUI
              limitTags={1}
              variant="standard"
              onChange={(_e, newVal, reason) => handleAutocompleteChange(fieldName, newVal, reason, cfg.control)}
              selectedValue={selectedValues}
              label={<FormLabelUI text={cfg.label} textColor="xl:text-white" />}
              dataset={autoCompleteOptions[cfg.optionsKey]}
              disabled={disabled}
              freeSolo
              multiple
              sx={{
                '@media (min-width:1140px)': {
                  ...sxConfig.fieldColor,
                },
              }}
            />
          </div>
        );
      })}
      {getKanbanFieldsByControl(type, 'switch').map(([fieldName, cfg]) => {
        return (
          <div key={fieldName} className={cfg.widthClass}>
            <SwitchUI
              name={fieldName}
              checked={filterFormState[fieldName] || false}
              label={<FormLabelUI text={cfg.label} textColor="xl:text-white" />}
              onChange={handleSwitchChange}
            />
          </div>
        );
      })}
      {getKanbanFieldsByControl(type, 'datepicker').map(([fieldName, cfg]) => {
        const disabled = cfg.getDisabled(roles);

        if (cfg.shouldShow && !cfg.shouldShow(params, type)) return null;

        return (
          <div key={fieldName} className={cfg.widthClass}>
            <DatePickerUI
              variant="standard"
              onChange={(newVal) => handleSimpleFilterChange(fieldName, newVal ? dayjs(newVal).endOf('day') : null)}
              value={filterFormState[fieldName]}
              label={<FormLabelUI text={cfg.label} textColor="xl:text-white" />}
              disabled={disabled}
              sx={{
                '@media (min-width:1140px)': {
                  ...sxConfig.fieldColor,
                },
              }}
            />
          </div>
        );
      })}
    </>
  );
};

export default KanbanFilters;
